/**
 * SaveButton
 *
 * Sticky footer bar with Save button and last modified info.
 */
import React from "react";
import { Box, Button, Typography } from "@mui/material";

/**
 * SaveButton Component
 *
 * Displays the save button and last modified information in a sticky bottom bar.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onSave - Save handler
 * @param {string|null} props.lastModified - Last modified timestamp
 * @param {Function} props.formatLastModified - Function to format last modified date
 *
 * @component
 */
export default function SaveButton({
  loading,
  onSave,
  lastModified,
  formatLastModified,
  hasUnsavedChanges,
}) {
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
        zIndex: 10,
        py: 2,
        px: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: "70%",
          mx: "auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          {lastModified &&
            formatLastModified &&
            formatLastModified(lastModified) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.875rem",
                  fontStyle: "italic",
                  opacity: 0.8,
                  fontWeight: 500,
                }}
              >
                Last Modified: {formatLastModified(lastModified)}
              </Typography>
            )}
          {hasUnsavedChanges && (
            <Typography
              variant="body2"
              color="warning.main"
              sx={{ fontWeight: 600, mt: 1 }}
            >
              You have unsaved changes. Please save your configuration!
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={onSave}
          disabled={loading}
          sx={{
            py: 2.5,
            px: 5,
            borderRadius: 3,
            fontSize: "1.1rem",
            fontWeight: 600,
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          {loading ? "Saving..." : "Save Slideshow Configuration"}
        </Button>
      </Box>
    </Box>
  );
}
